import type { TranslationKey } from '@infra/i18n';
import { APP_ROUTES } from '@shared/constants';
import { ACCESS_ROLES, type AccessRole } from './access.types';

/** Portals the app serves. Each role lands on one of them. */
export const PORTAL_KEYS = ['admin', 'org', 'user'] as const;
export type PortalKey = (typeof PORTAL_KEYS)[number];

/** What a role may do in the UI, named by the action, never by the role. */
export const ACCESS_CAPABILITIES = [
  // Narrow lists to one organization.
  'org.filter',
  // Download the expert review file of a session.
  'review.export',
  // Enter the organization portal.
  'org.portal',
  // Practise as a learner on /user/*.
  'practice',
  // See one's own coaching sessions.
  'coaching.own',
  // View the assigned team and its members.
  'team.view',
  // Open the profile page.
  'profile.view',
  // Book practice slots and answer reminders.
  'booking.own',
  // Configure booking and manage other people's bookings.
  'booking.manage',
] as const;
export type AccessCapability = (typeof ACCESS_CAPABILITIES)[number];

export interface RoleDef {
  portal: PortalKey;
  home: string;
  capabilities: readonly AccessCapability[];
}

export const ROLE_REGISTRY = {
  super_admin: {
    portal: 'admin',
    home: APP_ROUTES.ADMIN_DASHBOARD,
    capabilities: ['org.filter', 'review.export', 'booking.manage'],
  },
  org_admin: {
    portal: 'org',
    home: APP_ROUTES.ORG_DASHBOARD,
    capabilities: ['org.portal', 'profile.view', 'booking.manage'],
  },
  team_manager: {
    portal: 'user',
    home: APP_ROUTES.USER_DASHBOARD,
    capabilities: ['practice', 'coaching.own', 'team.view', 'profile.view', 'booking.own'],
  },
  member: {
    portal: 'user',
    home: APP_ROUTES.USER_DASHBOARD,
    capabilities: ['practice', 'coaching.own', 'profile.view', 'booking.own'],
  },
} as const satisfies Record<AccessRole, RoleDef>;

export function roleCan(role: AccessRole, capability: AccessCapability): boolean {
  return (ROLE_REGISTRY[role].capabilities as readonly AccessCapability[]).includes(capability);
}

/** Roles holding a capability, for gates that take a role list. */
export function rolesWithCapability(capability: AccessCapability): AccessRole[] {
  return ACCESS_ROLES.filter((role) => roleCan(role, capability));
}

/** Home route after login and after a role change. */
export function homePathForAccessRole(role: AccessRole): string {
  return ROLE_REGISTRY[role].home;
}

export function portalForAccessRole(role: AccessRole): PortalKey {
  return ROLE_REGISTRY[role].portal;
}

/** Membership role inside an organization. */
export const ORG_ROLES = ['org_admin', 'team_manager', 'member'] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

/** Org roles set directly on a user; team manager is assigned on the team. */
export type AssignableOrgRole = Exclude<OrgRole, 'team_manager'>;

export function isOrgRole(value: unknown): value is OrgRole {
  return typeof value === 'string' && (ORG_ROLES as readonly string[]).includes(value);
}

const ORG_ROLE_LABEL_KEYS = {
  org_admin: 'common:orgPortal.orgAdmin',
  team_manager: 'common:orgPortal.teamManager',
  member: 'common:orgPortal.member',
} as const satisfies Record<OrgRole, TranslationKey>;

export function orgRoleLabelKey(role: OrgRole): (typeof ORG_ROLE_LABEL_KEYS)[OrgRole] {
  return ORG_ROLE_LABEL_KEYS[role];
}
